import { AdPackageConfig } from "@/sell/models/checkout/config/AdPackageConfig";
import { AdUpgradeConfig } from "@/sell/models/checkout/config/AdUpgradeConfig";
import { AdUpgradeVariantConfig } from "@/sell/models/checkout/config/AdUpgradeVariantConfig";
import { AdCheckout } from "@/sell/models/checkout/schema/AdCheckout";
import { AdProductChoice } from "@/sell/models/checkout/schema/AdProductChoice";
import {
  AdUpgradeStateProps,
  AdUpgradeStatus,
} from "@/sell/models/checkout/state/AdUpgradeState";
import { AdUpgradeVariantStatus } from "@/sell/models/checkout/state/AdUpgradeVariantState";
import { addDays, isAfter } from "date-fns";
import { CurrencyService } from "./CurrencyService";
import { AdConfigState } from "@/sell/models/AdConfigState";

function GetPurchasesForUpgrade({
  purchases,
  upgrade,
}: {
  purchases: AdProductChoice[];
  upgrade: AdUpgradeConfig;
}): AdProductChoice[] {
  return purchases.filter(
    (purchase) => purchase.key && purchase.key.product === upgrade.key
  );
}

function GetCurrentExpiryForUpgrade({
  config,
  upgrade,
}: {
  config: AdConfigState;
  upgrade: AdUpgradeConfig;
}): Date | undefined {
  const productConfig = config.products?.[upgrade.key];

  if (productConfig && "expiresAt" in productConfig) {
    return productConfig.expiresAt;
  }
}

function GetPendingExpiryForUpgrade({
  checkout,
  upgrade,
  includedVariant,
  currentExpiry,
}: {
  checkout: AdCheckout;
  upgrade: AdUpgradeConfig;
  includedVariant?: AdUpgradeVariantConfig;
  currentExpiry?: Date;
}): Date | undefined {
  const pendingPurchase = GetPurchasesForUpgrade({
    purchases: checkout.pending,
    upgrade,
  })[0];
  const pendingVariant = upgrade.variants
    .filter((x) => x.key === pendingPurchase?.key.variant)
    .filter((x) => x.lifeCycle === "EXPIRING")[0];

  if (pendingVariant || includedVariant) {
    let pendingExpiry = new Date();
    if (currentExpiry && isAfter(currentExpiry, pendingExpiry)) {
      pendingExpiry = currentExpiry;
    }
    if (includedVariant && includedVariant.lifeCycle === "EXPIRING") {
      pendingExpiry = addDays(pendingExpiry, includedVariant.durationDays);
    }
    if (pendingVariant) {
      pendingExpiry = addDays(pendingExpiry, pendingVariant.durationDays);
    }
    return pendingExpiry;
  }
}

function GetNextExpiresOn({
  pendingExpiry,
  currentExpiry,
}: {
  pendingExpiry?: Date;
  currentExpiry?: Date;
}): Date | undefined {
  const pendingIsAfterCurrent =
    !currentExpiry || (pendingExpiry && isAfter(pendingExpiry, currentExpiry));
  return pendingIsAfterCurrent ? pendingExpiry : currentExpiry;
}

function GetIncludedVariant({
  adPackage,
  upgrade,
}: {
  adPackage?: AdPackageConfig;
  upgrade: AdUpgradeConfig;
}): AdUpgradeVariantConfig | undefined {
  const inclusionKey = adPackage?.inclusions.find(
    (x) => x.product === upgrade.key
  );
  if (inclusionKey) {
    return upgrade.variants.find((v) => v.key === inclusionKey.variant);
  }
}

function GetStatusForUpgrade({
  checkout,
  upgrade,
  includedVariant,
}: {
  checkout: AdCheckout;
  upgrade: AdUpgradeConfig;
  includedVariant: AdUpgradeVariantConfig | undefined;
}): {
  status: AdUpgradeStatus;
  pendingVariant?: AdUpgradeVariantConfig;
} {
  const pendingVariant = upgrade.variants?.find((v) =>
    checkout.pending.find(
      (p) => p.key.product === upgrade.key && p.key.variant === v.key
    )
  );
  let status: AdUpgradeStatus;
  if (includedVariant) {
    status = "INCLUDED";
  } else if (pendingVariant) {
    status = "ADDED";
  } else {
    status = "AVAILABLE";
  }

  return {
    status,
    pendingVariant,
  };
}

function GetStatusForUpgradeVariant({
  upgradeVariant,
  includedVariant,
  pendingVariant,
}: {
  upgradeVariant: AdUpgradeVariantConfig;
  includedVariant?: AdUpgradeVariantConfig;
  pendingVariant?: AdUpgradeVariantConfig;
}): AdUpgradeVariantStatus {
  if (includedVariant && upgradeVariant.key === includedVariant.key) {
    return "INCLUDED";
  } else if (pendingVariant && upgradeVariant.key === pendingVariant.key) {
    return "ADDED";
  } else {
    return "AVAILABLE";
  }
}

export const CheckoutUpgradeService = {
  GetState: function ({
    checkout,
    config,
    upgrade,
    pendingPackage,
    purchasedPackage,
  }: {
    checkout: AdCheckout;
    config: AdConfigState;
    upgrade: AdUpgradeConfig;
    pendingPackage?: AdPackageConfig;
    purchasedPackage?: AdPackageConfig;
  }): AdUpgradeStateProps {
    const pendingIncludedVariant = GetIncludedVariant({
      adPackage: pendingPackage,
      upgrade: upgrade,
    });
    const purchasedIncludedVariant = GetIncludedVariant({
      adPackage: purchasedPackage,
      upgrade: upgrade,
    });
    const includedVariant = !purchasedIncludedVariant
      ? pendingIncludedVariant
      : undefined;

    const { status, pendingVariant } = GetStatusForUpgrade({
      checkout,
      upgrade,
      includedVariant,
    });

    const currentExpiry = GetCurrentExpiryForUpgrade({
      config,
      upgrade,
    });
    const pendingExpiry = GetPendingExpiryForUpgrade({
      checkout,
      upgrade,
      currentExpiry,
      includedVariant,
    });
    const nextExpiry = GetNextExpiresOn({
      currentExpiry,
      pendingExpiry,
    });

    return {
      status: status,
      variants: upgrade.variants.map((v) => ({
        ...v,
        price: CurrencyService.EncodeDollars(v.price),
        status: GetStatusForUpgradeVariant({
          upgradeVariant: v,
          includedVariant,
          pendingVariant,
        }),
      })),
      expiries: {
        current: currentExpiry,
        next: nextExpiry,
      },
      price: CurrencyService.EncodeDollars(upgrade.variants[0]?.price || 0),
    };
  },
};
